import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { 
  Send, Mail, MessageCircle, Users, Building2, UserCheck, Briefcase,
  Search, RefreshCw, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SendModal - Modal reutilizável para envio de avaliações, DDS e treinamentos
 * 
 * Props:
 * - open: boolean - controla visibilidade do modal
 * - onOpenChange: function - callback quando modal abre/fecha
 * - title: string - título do modal (ex: "Enviar Treinamento")
 * - itemTitle: string - nome do item sendo enviado
 * - itemDescription: string - descrição do item
 * - onSend: function(method, selectedIds) - callback com método (email/whatsapp/both) e IDs dos funcionários
 * - showMethodSelector: boolean - mostrar seleção de email/whatsapp (default true)
 * - defaultMethod: string - método padrão ('email', 'whatsapp' ou 'both')
 */
const SendModal = ({ 
  open, 
  onOpenChange, 
  title = "Enviar",
  itemTitle = "",
  itemDescription = "",
  onSend,
  showMethodSelector = true,
  defaultMethod = 'both'
}) => {
  const [employees, setEmployees] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Filter states
  const [filterMode, setFilterMode] = useState('all'); // all, sector, cargo, individual
  const [sendMethod, setSendMethod] = useState(defaultMethod);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedCargos, setSelectedCargos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (open) {
      fetchData();
      // Reset state when opening
      setFilterMode('all');
      setSelectedEmployees([]);
      setSelectedSectors([]);
      setSelectedCargos([]);
      setSearchTerm('');
      setSendMethod(defaultMethod);
    }
  }, [open, defaultMethod]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [employeesRes, sectorsRes] = await Promise.all([
        axios.get(`${API_URL}/employees`, { headers }),
        axios.get(`${API_URL}/employees/sectors`, { headers })
      ]);
      
      const activeEmployees = employeesRes.data.filter(e => e.status === 'ativo');
      setEmployees(activeEmployees);
      setSectors(sectorsRes.data);
      
      // Extract unique cargos
      const uniqueCargos = [...new Set(activeEmployees.map(e => e.cargo).filter(Boolean))];
      const cargoStats = uniqueCargos.map(cargo => ({
        nome: cargo,
        funcionarios: activeEmployees.filter(e => e.cargo === cargo).length
      }));
      setCargos(cargoStats);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  // Get selected employees based on filter mode
  const getSelectedEmployeeIds = () => {
    if (filterMode === 'all') {
      return employees.map(e => e.id);
    }
    if (filterMode === 'sector') {
      return employees.filter(e => selectedSectors.includes(e.setor)).map(e => e.id);
    }
    if (filterMode === 'cargo') {
      return employees.filter(e => selectedCargos.includes(e.cargo)).map(e => e.id);
    }
    return selectedEmployees;
  };

  // Get count of selected employees
  const getSelectedCount = () => {
    return getSelectedEmployeeIds().length;
  };

  // Filter employees for display
  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return emp.nome?.toLowerCase().includes(search) ||
           emp.email?.toLowerCase().includes(search) ||
           emp.cargo?.toLowerCase().includes(search) ||
           emp.setor?.toLowerCase().includes(search);
  });

  const handleSelectAll = () => {
    if (filterMode === 'individual') {
      if (selectedEmployees.length === filteredEmployees.length) {
        setSelectedEmployees([]);
      } else {
        setSelectedEmployees(filteredEmployees.map(e => e.id));
      }
    }
  };

  const handleSend = async () => {
    const ids = getSelectedEmployeeIds();
    if (ids.length === 0) {
      toast.error('Selecione pelo menos um funcionário');
      return;
    }
    
    setSending(true);
    try {
      await onSend(sendMethod, ids, {
        filterMode,
        selectedSectors,
        selectedCargos
      });
      onOpenChange(false);
    } catch (error) {
      // Error handled by parent
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-slate-900 dark:text-white">
            {title}
          </DialogTitle>
          {itemTitle && (
            <DialogDescription className="pt-2">
              <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border">
                <p className="font-medium text-slate-900 dark:text-white text-sm">{itemTitle}</p>
                {itemDescription && (
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{itemDescription}</p>
                )}
              </div>
            </DialogDescription>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Método de Envio - Botões pequenos e elegantes */}
            {showMethodSelector && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Método de Envio
                </Label>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant={sendMethod === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => setSendMethod('whatsapp')}
                    size="sm"
                    className={`flex items-center gap-2 ${
                      sendMethod === 'whatsapp' 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'
                    }`}
                    data-testid="method-whatsapp"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </Button>
                  <Button 
                    type="button"
                    variant={sendMethod === 'email' ? 'default' : 'outline'}
                    onClick={() => setSendMethod('email')}
                    size="sm"
                    className={`flex items-center gap-2 ${
                      sendMethod === 'email' 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                        : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'
                    }`}
                    data-testid="method-email"
                  >
                    <Mail size={16} />
                    E-mail
                  </Button>
                  <Button 
                    type="button"
                    variant={sendMethod === 'both' ? 'default' : 'outline'}
                    onClick={() => setSendMethod('both')}
                    size="sm"
                    className={`flex items-center gap-2 ${
                      sendMethod === 'both' 
                        ? 'bg-[#0d9488] hover:bg-[#0b7d73] text-white' 
                        : 'hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300'
                    }`}
                    data-testid="method-both"
                  >
                    <Send size={16} />
                    Ambos
                  </Button>
                </div>
              </div>
            )}

            {/* Filtrar Destinatários - 4 opções: Todos, Por Setor, Por Cargo, Individual */}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                Filtrar Destinatários
              </Label>
              <div className="grid grid-cols-4 gap-2">
                <Button
                  type="button"
                  variant={filterMode === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilterMode('all')}
                  size="sm"
                  className={`flex flex-col items-center h-auto py-2 px-2 ${
                    filterMode === 'all' 
                      ? 'bg-[#0d9488] hover:bg-[#0b7d73] text-white' 
                      : ''
                  }`}
                  data-testid="filter-all"
                >
                  <Users size={18} className="mb-1" />
                  <span className="text-xs font-medium">Todos</span>
                </Button>
                <Button
                  type="button"
                  variant={filterMode === 'sector' ? 'default' : 'outline'}
                  onClick={() => setFilterMode('sector')}
                  size="sm"
                  className={`flex flex-col items-center h-auto py-2 px-2 ${
                    filterMode === 'sector' 
                      ? 'bg-[#0d9488] hover:bg-[#0b7d73] text-white' 
                      : ''
                  }`}
                  data-testid="filter-sector"
                >
                  <Building2 size={18} className="mb-1" />
                  <span className="text-xs font-medium">Por Setor</span>
                </Button>
                <Button
                  type="button"
                  variant={filterMode === 'cargo' ? 'default' : 'outline'}
                  onClick={() => setFilterMode('cargo')}
                  size="sm"
                  className={`flex flex-col items-center h-auto py-2 px-2 ${
                    filterMode === 'cargo' 
                      ? 'bg-[#0d9488] hover:bg-[#0b7d73] text-white' 
                      : ''
                  }`}
                  data-testid="filter-cargo"
                >
                  <Briefcase size={18} className="mb-1" />
                  <span className="text-xs font-medium">Por Cargo</span>
                </Button>
                <Button
                  type="button"
                  variant={filterMode === 'individual' ? 'default' : 'outline'}
                  onClick={() => setFilterMode('individual')}
                  size="sm"
                  className={`flex flex-col items-center h-auto py-2 px-2 ${
                    filterMode === 'individual' 
                      ? 'bg-[#0d9488] hover:bg-[#0b7d73] text-white' 
                      : ''
                  }`}
                  data-testid="filter-individual"
                >
                  <UserCheck size={18} className="mb-1" />
                  <span className="text-xs font-medium">Individual</span>
                </Button>
              </div>
            </div>

            {/* Seleção por Setor */}
            {filterMode === 'sector' && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Selecione os Setores
                </Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white dark:bg-slate-900">
                  {sectors.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nenhum setor encontrado</p>
                  ) : (
                    sectors.map((sector) => (
                      <label 
                        key={sector.nome} 
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedSectors.includes(sector.nome)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSectors([...selectedSectors, sector.nome]);
                            } else {
                              setSelectedSectors(selectedSectors.filter(s => s !== sector.nome));
                            }
                          }}
                        />
                        <div className="flex-1">
                          <span className="font-medium text-sm text-slate-900 dark:text-white">{sector.nome}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">{sector.funcionarios} func.</Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Seleção por Cargo */}
            {filterMode === 'cargo' && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Selecione os Cargos
                </Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white dark:bg-slate-900">
                  {cargos.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nenhum cargo encontrado</p>
                  ) : (
                    cargos.map((cargo) => (
                      <label 
                        key={cargo.nome} 
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedCargos.includes(cargo.nome)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCargos([...selectedCargos, cargo.nome]);
                            } else {
                              setSelectedCargos(selectedCargos.filter(c => c !== cargo.nome));
                            }
                          }}
                        />
                        <div className="flex-1">
                          <span className="font-medium text-sm text-slate-900 dark:text-white">{cargo.nome}</span>
                        </div>
                        <Badge variant="secondary" className="text-xs">{cargo.funcionarios} func.</Badge>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Seleção Individual */}
            {filterMode === 'individual' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Selecione os Funcionários
                  </Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={handleSelectAll}
                    className="text-xs h-7"
                  >
                    {selectedEmployees.length === filteredEmployees.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                  </Button>
                </div>
                
                {/* Search */}
                <div className="relative mb-2">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por nome, email, cargo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                
                <div className="max-h-52 overflow-y-auto border rounded-lg p-2 space-y-1 bg-white dark:bg-slate-900">
                  {filteredEmployees.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nenhum funcionário encontrado</p>
                  ) : (
                    filteredEmployees.map((emp) => (
                      <label 
                        key={emp.id} 
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selectedEmployees.includes(emp.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedEmployees([...selectedEmployees, emp.id]);
                            } else {
                              setSelectedEmployees(selectedEmployees.filter(id => id !== emp.id));
                            }
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{emp.nome}</p>
                          <p className="text-xs text-slate-500 truncate">{emp.cargo}</p>
                        </div>
                        <span className="text-xs text-slate-400 truncate max-w-[120px] hidden sm:block">
                          {emp.email || 'Sem email'}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Summary para modo "Todos" */}
            {filterMode === 'all' && (
              <div className="bg-[#0d9488]/10 border border-[#0d9488]/20 rounded-lg p-3">
                <p className="text-sm text-[#0d9488] flex items-center gap-2">
                  <Users size={16} />
                  Será enviado para <strong>{employees.length}</strong> funcionário(s) ativo(s)
                </p>
              </div>
            )}

            {/* Summary para outros modos */}
            {(filterMode === 'sector' || filterMode === 'cargo') && getSelectedCount() > 0 && (
              <div className="bg-[#0d9488]/10 border border-[#0d9488]/20 rounded-lg p-3">
                <p className="text-sm text-[#0d9488] flex items-center gap-2">
                  <Users size={16} />
                  <strong>{getSelectedCount()}</strong> funcionário(s) serão notificados
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0 pt-4 border-t mt-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={sending}
            size="sm"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSend}
            disabled={sending || getSelectedCount() === 0}
            className="bg-[#0d9488] hover:bg-[#0b7d73]"
            size="sm"
            data-testid="send-button"
          >
            {sending ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send size={16} className="mr-2" />
                Enviar para {getSelectedCount()} funcionário(s)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendModal;
